import React, { useMemo } from 'react';
import { motion } from 'framer-motion';
import { useMotionBudget } from '../../../app/hooks/useMotionBudget';
const SPARKLE_COLOR_MAP: Record<string, string> = {
  amber: 'bg-amber-400',
  purple: 'bg-purple-400',
  fuchsia: 'bg-fuchsia-400'
};
/**
 * SparkleParticles — floating motes for rare/legendary cards.
 * Positions are computed once per mount (stable across re-renders).
 * Suppressed entirely when the user prefers reduced motion.
 */
export function SparkleParticles({
  count = 6,
  color = 'amber'
}: {
  count?: number;
  color?: string;
}) {
  const {
    shouldAnimate
  } = useMotionBudget();
  const positions = useMemo(() => Array.from({
    length: count
  }).map(() => ({
    left: `${15 + Math.random() * 70}%`,
    top: `${15 + Math.random() * 70}%`
  })), [count]);
  if (!shouldAnimate) return null;
  return <div className="absolute inset-0 overflow-hidden pointer-events-none" aria-hidden="true" data-id="element-1873" data-mp="1d1f9vhpc7tnf">
      
      {positions.map((pos, i) => <motion.div key={i} className={`absolute w-1.5 h-1.5 rounded-full ${SPARKLE_COLOR_MAP[color] || SPARKLE_COLOR_MAP.amber}`} style={pos} animate={{
      scale: [0, 1, 0],
      opacity: [0, 1, 0],
      y: [0, -10, -20]
    }} transition={{
      duration: 2,
      repeat: Infinity,
      delay: i * 0.3,
      ease: 'easeOut'
    }} data-id="element-1874" data-mp="1c7sumxbn8wdz" data-mpi={i} />)}
    </div>;
}
/** GoldenShimmer — sweeping highlight band for rare achievements. */
export function GoldenShimmer() {
  const {
    shouldAnimate
  } = useMotionBudget();
  if (!shouldAnimate) {
    // Static warm tint preserves the "special" feel without motion.
    return <div className="absolute inset-0 rounded-2xl overflow-hidden pointer-events-none bg-amber-200/15" aria-hidden="true" data-id="element-1875" data-mp="24wq1836ht4qw" />;
  }
  return <motion.div className="absolute inset-0 rounded-2xl overflow-hidden pointer-events-none" aria-hidden="true" initial={{
    opacity: 0
  }} animate={{
    opacity: 1
  }} data-id="element-1876" data-mp="29u8bbww8xq0h">
      
      <motion.div className="absolute inset-0 bg-gradient-to-r from-transparent via-amber-200/40 to-transparent" animate={{
      x: ['-100%', '200%']
    }} transition={{
      duration: 3,
      repeat: Infinity,
      repeatDelay: 2,
      ease: 'easeInOut'
    }} data-id="element-1877" data-mp="fgqlu4ca6j2t" />
      
    </motion.div>;
}
/** LegendaryGlow — pulsing aura behind legendary cards. */
export function LegendaryGlow() {
  const {
    shouldAnimate
  } = useMotionBudget();
  if (!shouldAnimate) {
    // Static glow ring — keeps the legendary distinction, drops the pulse.
    return <div className="absolute -inset-1 rounded-3xl bg-gradient-to-r from-purple-400 via-fuchsia-400 to-pink-400 opacity-25 blur-md pointer-events-none" aria-hidden="true" data-id="element-1878" data-mp="qdltnaq0x5vr" />;
  }
  return <motion.div className="absolute -inset-1 rounded-3xl bg-gradient-to-r from-purple-400 via-fuchsia-400 to-pink-400 opacity-30 blur-md pointer-events-none" aria-hidden="true" animate={{
    opacity: [0.2, 0.4, 0.2],
    scale: [1, 1.02, 1]
  }} transition={{
    duration: 2,
    repeat: Infinity,
    ease: 'easeInOut'
  }} data-id="element-1879" data-mp="104q5i1kekamd" />;
}